/* eslint-disable max-lines */
import React, { useContext, useMemo, useRef, useEffect, useCallback } from "react";
import { PLWidgetController, WidgetControllerContext } from "@swiggy-private/dls-react-mxn/dist";

import { Loader } from "@swiggy-private/dls-web-core/dist/components/Loader";
import { SwiggyContextHeader } from "@swiggy-private/portal-dineout-context";
import { IBaseMenuConfig } from "./types";
import { goToCartWidgetAnalyticsInfo, OBJECT_VALUES } from "../constants/analytics";
import { ErrorComponent } from "../ErrorComponent";
import { FloatingWidget } from "../FloatingWidget";
import { MENU_CONTAINER_ID } from "../Menu/constants";
import {
    BottomWidget,
    Container,
    FloatingWidgetContainer,
    FloatingWidgetPosition,
    MenuFlexContainer,
    OverLayLoader,
} from "../Menu/styles";
import { PLWidgetRenderer } from "../PLWidgetRenderer";

interface IBaseMenuProps {
    config: IBaseMenuConfig;
    onMenuLoad?: () => void;
}

const BaseMenu = ({ config, onMenuLoad }: IBaseMenuProps) => {
    const { swiggyHeaders } = useContext(SwiggyContextHeader);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const {
        cards,
        fabWidgetData,
        popupConfigs,
        loading,
        emptyItemCard,
        customWidgetTypeHandler,
        customWidgetTypes,
        clickAnalytics,
        impressionAnalytics,
        genericClickHandler,
        onInvalidWidget,
        onWidgetError,
        onEmptyPage,
        onScrollChange,
        showFloatingWidget = false,
        showBottomWidget = false,
        ifCartItemAvailable = false,
        goToCart,
        cartItemsCount = 0,
        emptyStateConfig,
        screenName,
    } = config;

    const goToCartImpression = useCallback(() => {
        impressionAnalytics?.({
            ...goToCartWidgetAnalyticsInfo,
            category: screenName,
            action: OBJECT_VALUES.GO_TO_CART_IMPRESSION_CONTAINER,
            context: JSON.stringify({
                item_count: cartItemsCount,
            }),
        });
    }, [impressionAnalytics, cartItemsCount, screenName]);

    const goToCartClick = useCallback(() => {
        clickAnalytics?.({
            ...goToCartWidgetAnalyticsInfo,
            category: screenName,
            action: OBJECT_VALUES.GO_TO_CART_CLICK_CONTAINER,
            context: JSON.stringify({
                item_count: cartItemsCount,
            }),
        });
    }, [clickAnalytics, cartItemsCount, screenName]);

    const contextValue = useMemo(() => {
        return {
            impressionAnalytics,
            clickAnalytics,
            clickHandler: genericClickHandler,
            swiggyHeaders,
            customWidgetTypeHandler,
            customWidgetTypes,
        };
    }, [
        clickAnalytics,
        customWidgetTypeHandler,
        customWidgetTypes,
        genericClickHandler,
        impressionAnalytics,
        swiggyHeaders,
    ]);

    // Scroll detection
    useEffect(() => {
        if (!onScrollChange) return;

        const handleScroll = () => {
            if (scrollContainerRef.current) {
                const scrollTop = scrollContainerRef.current.scrollTop;
                onScrollChange(scrollTop > 50); // Consider scrolled after 50px
            }
        };

        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
            return () => scrollContainer.removeEventListener("scroll", handleScroll);
        }
    }, [onScrollChange]);

    useEffect(() => {
        if (!loading) {
            onMenuLoad?.();
        }
    }, [onMenuLoad, loading]);

    if (loading) {
        return (
            <OverLayLoader>
                <Loader type="primary" size="large" />
            </OverLayLoader>
        );
    }

    return (
        <Container>
            <MenuFlexContainer id={MENU_CONTAINER_ID} ref={scrollContainerRef}>
                <WidgetControllerContext.Provider value={contextValue}>
                    <PLWidgetRenderer
                        popupConfigs={popupConfigs}
                        screenName={screenName}
                        onInvalidWidget={onInvalidWidget}
                        onWidgetError={onWidgetError}
                        onEmptyPage={onEmptyPage}
                        cards={cards}
                    />
                    {fabWidgetData && (
                        <FloatingWidgetPosition ifCartItemAvailable={ifCartItemAvailable}>
                            <PLWidgetController
                                onInvalidWidget={onInvalidWidget}
                                onWidgetError={onWidgetError}
                                onEmptyPage={onEmptyPage}
                                cards={fabWidgetData}
                            />
                        </FloatingWidgetPosition>
                    )}
                </WidgetControllerContext.Provider>

                {emptyItemCard && emptyStateConfig && (
                    <ErrorComponent
                        onCtaClick={() => {}}
                        showCta={false}
                        heading={emptyStateConfig.heading}
                        description={emptyStateConfig.description}
                        showFullPage={false}
                        imgSrc={emptyStateConfig.imgSrc}
                    />
                )}

                {showBottomWidget && <BottomWidget />}

                {showFloatingWidget && goToCart && (
                    <FloatingWidgetContainer>
                        <FloatingWidget
                            visible={ifCartItemAvailable}
                            rightContent={{ text: "Go to cart", color: "text_Button_Primary" }}
                            handler={goToCart}
                            leftContent={{
                                text: cartItemsCount > 0 ? `${cartItemsCount} item(s)` : "",
                                color: "text_Button_Primary",
                            }}
                            impressionAnalytics={goToCartImpression}
                            clickAnalytics={goToCartClick}
                        />
                    </FloatingWidgetContainer>
                )}
            </MenuFlexContainer>
        </Container>
    );
};

export default React.memo(BaseMenu);
